import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core'
import { UserWithSummary } from '@core/interfaces/kpis.interface'
import { FILTER_TYPES, FilterType } from '@participant/models/filter-types.enum'
import { SelectComponent } from '../select/select.component'
import { IconComponent } from '../icons/icons.component'
import { SearchComponent } from '../search/search.component'
import { NotFoundComponent } from '../not-found/not-found.component'
import { ClicksDirective } from '@shared/directives/clicks.directive'
import { PilotsItemComponent } from '@shared/components/pilots-item/pilots-item.component'

interface SegmentOption {
  key: string
  label: string
}

@Component({
  selector: 'users-list',
  imports: [SelectComponent, IconComponent, SearchComponent, ClicksDirective, NotFoundComponent, PilotsItemComponent],
  templateUrl: './users-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsersListComponent {
  usersWithSummary = input.required<UserWithSummary[]>()

  readonly FILTER_TYPES = FILTER_TYPES
  readonly SEGMENTS: SegmentOption[] = [
    { key: 'todos', label: 'TODOS' },
    { key: 'top5', label: 'TOP 5' },
    { key: 'a_motivar', label: 'A MOTIVAR' }
  ] as const

  searchTerm = signal('')
  selectedFilter = signal<FilterType>(FilterType.NAME_ASC)
  selectedSegment = signal<SegmentOption>(this.SEGMENTS[0])

  // Single computed that applies all filters in sequence
  filteredUsers = computed(() => {
    let users = [...this.usersWithSummary()]

    // 1. Apply search filter
    const search = this.searchTerm().trim().toLowerCase()
    if (search) {
      users = users.filter(user => user.name.toLowerCase().includes(search))
    }

    // 2. Apply segment filter
    const segment = this.selectedSegment().key
    if (segment === 'top5') {
      users = users.sort((a, b) => this.getPercentage(b) - this.getPercentage(a)).slice(0, 5)
    } else if (segment === 'a_motivar') {
      users = users.sort((a, b) => this.getPercentage(a) - this.getPercentage(b)).slice(0, 5)
    }

    // 3. Apply user's selected sort filter
    users = this.sortUsers(users, this.selectedFilter())

    return users
  })

  // Extracted sorting logic
  private sortUsers(users: UserWithSummary[], filterType: FilterType): UserWithSummary[] {
    const usersCopy = [...users]

    switch (filterType) {
      case FilterType.NAME_ASC:
        return usersCopy.sort((a, b) => a.name.localeCompare(b.name))

      case FilterType.NAME_DESC:
        return usersCopy.sort((a, b) => b.name.localeCompare(a.name))

      case FilterType.SCORE_DESC:
        return usersCopy.sort((a, b) => this.getPercentage(b) - this.getPercentage(a))

      case FilterType.SCORE_ASC:
        return usersCopy.sort((a, b) => this.getPercentage(a) - this.getPercentage(b))

      default:
        return usersCopy
    }
  }

  private getPercentage(user: UserWithSummary): number {
    return user.summary?.percentage ?? 0
  }
}
